
import React, { useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  ChevronRight,
  MapPin,
} from "lucide-react";

// Project images
import project1 from "../../assests/gallery/gallery1.webp";
import project2 from "../../assests/gallery/gallery1.webp";
import project3 from "../../assests/gallery/gallery1.webp";
import project4 from "../../assests/gallery/gallery1.webp";
import project5 from "../../assests/gallery/gallery1.webp";
import project6 from "../../assests/gallery/gallery1.webp";
import project7 from "../../assests/gallery/gallery1.webp";
import project8 from "../../assests/gallery/gallery1.webp";
import project9 from "../../assests/gallery/gallery1.webp";
import project10 from "../../assests/gallery/gallery1.webp";

const projects = [
  {
    id: 1,
    title: "Modern Home Interior",
    category: "Home Interiors",
    description:
      "A comfortable home with smart storage, a well-planned kitchen, stylish wardrobes and warm lighting.",
    image: project1,
  },
  {
    id: 2,
    title: "Office Interior Design",
    category: "Commercial Spaces",
    description:
      "A practical, modern office designed for comfortable working, better space use and a professional look.",
    image: project2,
  },
  {
    id: 3,
    title: "Complete Home Project",
    category: "Complete Projects",
    description:
      "From interior planning and construction to painting and final finishing, we help bring your home together.",
    image: project3,
  },
  {
    id: 4,
    title: "Home Makeover",
    category: "Home Interiors",
    description:
      "Refresh your home with better space planning, useful furniture and fresh colours.",
    image: project4,
  },
  {
    id: 5,
    title: "Commercial Space Design",
    category: "Commercial Spaces",
    description:
      "Create an attractive business space with thoughtful layouts, suitable lighting and clean finishes.",
    image: project5,
  },
  {
    id: 6,
    title: "Interior Painting",
    category: "Interior Painting",
    description:
      "Give your rooms a fresh look with carefully chosen colours and neat wall finishes.",
    image: project6,
  },
  {
    id: 7,
    title: "Premium Home Design",
    category: "Home Interiors",
    description:
      "A stylish home with elegant materials, thoughtful lighting and carefully planned interiors.",
    image: project7,
  },
  {
    id: 8,
    title: "Exterior Painting",
    category: "Exterior Painting",
    description:
      "Improve the outside look of your property with suitable colours and weather-resistant paint finishes.",
    image: project8,
  },
  {
    id: 9,
    title: "Home Renovation",
    category: "Complete Projects",
    description:
      "Update an existing property with improved interiors, fresh paint and practical finishing work.",
    image: project9,
  },
  {
    id: 10,
    title: "Decorative Wall Finishes",
    category: "Interior Painting",
    description:
      "Add character to your space with texture painting, feature walls and decorative finishes.",
    image: project10,
  },
];

const categories = [
  "All Projects",
  "Home Interiors",
  "Commercial Spaces",
  "Complete Projects",
  "Interior Painting",
  "Exterior Painting",
];

export default function Work() {
  const [activeCategory, setActiveCategory] = useState("All Projects");
  const [currentIndex, setCurrentIndex] = useState(0);

  const filteredProjects =
    activeCategory === "All Projects"
      ? projects
      : projects.filter(
          (project) => project.category === activeCategory
        );

  const activeProject =
    filteredProjects[
      Math.min(currentIndex, filteredProjects.length - 1)
    ];

  const nextSlide = () => {
    setCurrentIndex((prev) =>
      prev >= filteredProjects.length - 1 ? 0 : prev + 1
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prev) =>
      prev <= 0 ? filteredProjects.length - 1 : prev - 1
    );
  };

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    setCurrentIndex(0);
  };

  const goToContact = () => {
    const contact = document.getElementById("contact");

    if (contact) {
      contact.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    } else {
      window.location.href = "/contact/";
    }
  };

  return (
    <section
      id="work"
      aria-labelledby="work-heading"
      className="relative w-full overflow-hidden bg-slate-50 py-14 font-['DM_Sans',sans-serif] text-slate-900 sm:py-16 md:py-20 lg:py-24"
    >
      {/* Background decoration */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-0 top-0 -z-0 h-64 w-64 rounded-full bg-indigo-100/60 blur-[100px] sm:h-96 sm:w-96"
      />

      <div className="relative z-10 mx-auto w-full max-w-[1440px] px-5 sm:px-8 md:px-10 lg:px-12 xl:px-16">
        {/* HERO HEADER */}
        <div className="mx-auto mb-10 max-w-4xl text-center sm:mb-12 md:mb-16">
          <span className="inline-flex items-center rounded-full border border-indigo-100 bg-white px-4 py-2 font-['Poppins',sans-serif] text-[10px] font-semibold uppercase tracking-[0.2em] text-indigo-700 sm:text-xs">
            Selected Projects
          </span>

          <h2
            id="work-heading"
            className="mx-auto mt-5 max-w-4xl font-['Poppins',sans-serif] text-[clamp(2.2rem,5vw,4.5rem)] font-semibold leading-[1.12] tracking-[-0.045em] text-slate-950 sm:mt-6"
          >
            Beautiful Spaces.
            <span className="block text-indigo-600">
              Quality Finishes.
            </span>
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-sm leading-7 text-slate-600 sm:mt-6 sm:text-base sm:leading-8 md:text-lg">
            Explore our interior design, construction and painting work.
            From modern homes and offices to fresh wall colours and
            decorative finishes, Cube4Spaces and Paint Studio help make
            every space look its best.
          </p>
        </div>

        {/* CATEGORY FILTERS */}
        <div className="mb-10 sm:mb-12 lg:mb-16">
          <p className="mb-4 text-center font-['Poppins',sans-serif] text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 sm:text-xs">
            Explore by Category
          </p>

          <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
            {categories.map((category) => {
              const isActive = activeCategory === category;

              return (
                <button
                  key={category}
                  type="button"
                  onClick={() => handleCategoryChange(category)}
                  aria-pressed={isActive}
                  className={`min-h-10 rounded-full border px-4 py-2 font-['Poppins',sans-serif] text-[11px] font-medium transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:min-h-11 sm:px-5 sm:text-xs md:px-6 ${
                    isActive
                      ? "border-indigo-600 bg-indigo-600 text-white"
                      : "border-slate-200 bg-white text-slate-600 hover:border-indigo-300 hover:text-indigo-700"
                  }`}
                >
                  {category}
                </button>
              );
            })}
          </div>
        </div>

        {/* PROJECT SHOWCASE */}
        <div className="mx-auto max-w-7xl overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:rounded-3xl sm:p-6 md:p-8 lg:p-10">
          <div className="mb-6 flex flex-col gap-5 sm:mb-8 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <span className="font-['Poppins',sans-serif] text-[10px] font-semibold uppercase tracking-[0.18em] text-indigo-600 sm:text-xs">
                Project Showcase
              </span>

              <h3 className="mt-2 font-['Poppins',sans-serif] text-2xl font-semibold tracking-tight text-slate-950 sm:text-3xl md:text-4xl">
                Our Recent Work
              </h3>

              <p className="mt-3 max-w-xl text-sm leading-6 text-slate-600 sm:text-base sm:leading-7">
                Discover how we improve spaces through smart design,
                careful construction and quality painting.
              </p>
            </div>

            <div className="flex items-center justify-between gap-4 sm:justify-end">
              <span className="text-xs font-medium text-slate-500 sm:text-sm">
                {String(currentIndex + 1).padStart(2, "0")} /{" "}
                {String(filteredProjects.length).padStart(2, "0")}
              </span>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={prevSlide}
                  disabled={filteredProjects.length <= 1}
                  aria-label="Previous project"
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 transition-colors hover:border-indigo-300 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-40 sm:h-11 sm:w-11"
                >
                  <ArrowLeft size={18} strokeWidth={1.6} />
                </button>

                <button
                  type="button"
                  onClick={nextSlide}
                  disabled={filteredProjects.length <= 1}
                  aria-label="Next project"
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-950 text-white transition-colors hover:bg-indigo-600 disabled:cursor-not-allowed disabled:opacity-40 sm:h-11 sm:w-11"
                >
                  <ArrowRight size={18} strokeWidth={1.6} />
                </button>
              </div>
            </div>
          </div>

          {/* Featured project */}
          {activeProject && (
            <article
              key={`${activeCategory}-${activeProject.id}`}
              className="grid min-w-0 grid-cols-1 overflow-hidden rounded-xl border border-slate-100 bg-slate-50 sm:rounded-2xl lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]"
            >
              <div className="relative min-h-[240px] overflow-hidden bg-slate-200 sm:min-h-[340px] lg:min-h-[470px]">
                <img
                  src={activeProject.image}
                  alt={activeProject.title}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 h-full w-full object-cover"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/45 via-transparent to-transparent" />

                <span className="absolute bottom-4 left-4 rounded-full bg-white/95 px-3 py-1.5 font-['Poppins',sans-serif] text-[10px] font-semibold uppercase tracking-wider text-indigo-700 sm:bottom-6 sm:left-6 sm:px-4 sm:py-2 sm:text-xs">
                  {activeProject.category}
                </span>
              </div>

              <div className="flex min-w-0 flex-col justify-center p-5 sm:p-7 md:p-9 lg:p-10 xl:p-12">
                <span className="font-['Poppins',sans-serif] text-[10px] font-semibold uppercase tracking-[0.18em] text-indigo-600 sm:text-xs">
                  Project{" "}
                  {String(activeProject.id).padStart(2, "0")}
                </span>

                <h4 className="mt-3 font-['Poppins',sans-serif] text-2xl font-semibold leading-tight tracking-tight text-slate-950 sm:text-3xl lg:text-4xl">
                  {activeProject.title}
                </h4>

                <p className="mt-4 text-sm leading-7 text-slate-600 sm:mt-5 sm:text-base sm:leading-8">
                  {activeProject.description}
                </p>

                <div className="mt-6 border-t border-slate-200 pt-5 sm:mt-8 sm:pt-6">
                  <p className="text-xs leading-6 text-slate-500 sm:text-sm">
                    Part of our interior design, construction and finishing
                    service offering.
                  </p>

                  <button
                    type="button"
                    onClick={goToContact}
                    className="mt-5 inline-flex min-h-11 items-center gap-3 rounded-lg bg-slate-950 px-5 py-3 font-['Poppins',sans-serif] text-xs font-semibold text-white transition-colors hover:bg-indigo-600 sm:mt-6 sm:px-6 sm:text-sm"
                  >
                    Discuss Your Project
                    <ArrowUpRight size={17} strokeWidth={1.6} />
                  </button>
                </div>
              </div>
            </article>
          )}

          {/* Thumbnail navigation */}
          <div className="mt-6 grid grid-cols-2 gap-3 sm:mt-8 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-5">
            {filteredProjects.map((project, index) => {
              const isActive = index === currentIndex;

              return (
                <button
                  key={project.id}
                  type="button"
                  onClick={() => setCurrentIndex(index)}
                  aria-pressed={isActive}
                  aria-label={`View project: ${project.title}`}
                  className={`group min-w-0 overflow-hidden rounded-lg border text-left transition-colors sm:rounded-xl ${
                    isActive
                      ? "border-indigo-600 bg-indigo-50"
                      : "border-slate-200 bg-white hover:border-indigo-300"
                  }`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
                    <img
                      src={project.image}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>

                  <div className="p-3 sm:p-3.5">
                    <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-500 sm:text-[10px]">
                      Project {String(project.id).padStart(2, "0")}
                    </span>

                    <p className="mt-1 line-clamp-2 font-['Poppins',sans-serif] text-xs font-medium leading-5 text-slate-900 sm:text-sm">
                      {project.title}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* OUR WORK IN NUMBERS / SERVICE SUMMARY */}
        <div className="mt-12 grid grid-cols-1 gap-4 sm:mt-16 sm:grid-cols-2 lg:mt-20 lg:grid-cols-4">
          {[
            {
              number: "01",
              title: "Interior Design",
              description: "Thoughtful spaces planned around your needs.",
            },
            {
              number: "02",
              title: "Construction",
              description: "Coordinated planning and building services.",
            },
            {
              number: "03",
              title: "Painting",
              description: "Interior and exterior painting solutions.",
            },
            {
              number: "04",
              title: "Service Area",
              description: "Coimbatore and nearby locations.",
            },
          ].map((item) => (
            <article
              key={item.number}
              className="min-w-0 rounded-xl border border-slate-200 bg-white p-5 sm:rounded-2xl sm:p-6"
            >
              <span className="font-['Poppins',sans-serif] text-xs font-semibold tracking-wider text-indigo-600">
                {item.number}
              </span>

              <h4 className="mt-3 font-['Poppins',sans-serif] text-lg font-semibold text-slate-950 sm:text-xl">
                {item.title}
              </h4>

              <p className="mt-2 text-sm leading-6 text-slate-600">
                {item.description}
              </p>
            </article>
          ))}
        </div>

        {/* FINAL CTA */}
        <div className="relative mt-14 overflow-hidden rounded-2xl bg-slate-950 p-6 text-white sm:mt-20 sm:rounded-3xl sm:p-8 md:p-10 lg:p-14">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-16 -top-20 h-48 w-48 rounded-full bg-indigo-600/20 blur-3xl sm:h-72 sm:w-72"
          />

          <div className="relative grid grid-cols-1 items-center gap-7 lg:grid-cols-[minmax(0,1fr)_auto] lg:gap-10">
            <div className="min-w-0">
              <span className="font-['Poppins',sans-serif] text-[10px] font-semibold uppercase tracking-[0.18em] text-indigo-300 sm:text-xs">
                Have a Project in Mind?
              </span>

              <h3 className="mt-3 max-w-3xl font-['Poppins',sans-serif] text-2xl font-semibold leading-tight tracking-tight sm:mt-4 sm:text-3xl md:text-4xl lg:text-5xl">
                Let's Make Your Space Better.
              </h3>

              <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-300 sm:text-base sm:leading-8">
                Planning a new home, updating your office, building a
                property or repainting your walls? Tell us what you need,
                and our team will help you plan the next step.
              </p>

              <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-slate-300 sm:text-sm">
                <span>Cube4Spaces</span>
                <span className="text-indigo-400">|</span>
                <span>Paint Studio</span>
              </div>
            </div>

            <div className="flex w-full flex-col gap-3 sm:flex-row lg:w-auto lg:flex-col">
              <button
                type="button"
                onClick={goToContact}
                className="inline-flex min-h-12 w-full items-center justify-center gap-3 rounded-lg bg-white px-6 py-3.5 font-['Poppins',sans-serif] text-sm font-semibold text-slate-950 transition-colors hover:bg-indigo-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white sm:flex-1 lg:min-w-[220px]"
              >
                Start Your Project
                <ArrowRight size={18} strokeWidth={1.6} />
              </button>

              <a
                href="/paint-studio-coimbatore/"
                className="inline-flex min-h-12 w-full items-center justify-center gap-3 rounded-lg border border-white/25 px-6 py-3.5 font-['Poppins',sans-serif] text-sm font-semibold text-white transition-colors hover:border-white hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white sm:flex-1 lg:min-w-[220px]"
              >
                Explore Painting Services
                <ChevronRight size={18} strokeWidth={1.6} />
              </a>
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="mt-7 flex flex-col gap-3 border-t border-slate-200 pt-6 text-xs leading-6 text-slate-500 sm:mt-9 sm:flex-row sm:items-center sm:justify-between">
          <p>
            <span className="font-semibold text-slate-800">
              Cube4Spaces
            </span>
            {" "}— Interior Design & Construction
          </p>

          <p>
            <span className="font-semibold text-slate-800">
              Paint Studio
            </span>
            {" "}— Painting & Finishing Services
          </p>

          <p className="inline-flex items-center gap-2">
            <MapPin size={14} strokeWidth={1.5} aria-hidden="true" />
            Coimbatore and nearby areas
          </p>
        </div>
      </div>
    </section>
  );
}
